import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ShieldCheck, ArrowLeft, FileText, Download } from 'lucide-react';
import { getBackendUrl } from '../services/origin';

const API = getBackendUrl();

// Public, deploy-agnostic privacy policy renderer.
//
// Design notes:
//  • Lives at /privacy-policy on the customer origin so internal links
//    (registration page, consent flow, marketing emails) survive any
//    domain or deploy change without breaking.
//  • Reads `/api/public/privacy-policy` which serves the latest brand
//    privacy version (and lazy-generates AI sections if missing).
//  • Falls back to the uploaded PDF (`privacy_policy_file_url`) if the
//    structured JSON sections are unavailable — old admin-uploaded
//    custom PDFs still work.
export default function PrivacyPolicyPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    document.title = 'Informativa Privacy';
    axios
      .get(`${API}/api/public/privacy-policy`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setError('Impossibile caricare la privacy policy.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const branding = data?.branding || {};
  const bg = branding.background_color || '#111114';
  const tc = branding.text_color || '#FFFFFF';
  const ts = branding.text_secondary_color || '#94A3B8';
  const pc = branding.primary_color || '#D44E20';

  const sections = data?.privacy_policy_sections || [];
  const fileUrl = data?.privacy_policy_file_url
    ? (data.privacy_policy_file_url.startsWith('http')
        ? data.privacy_policy_file_url
        : `${API}${data.privacy_policy_file_url}`)
    : null;
  const externalUrl = data?.privacy_policy_url;
  const businessName = data?.business_name || '';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: bg }}>
        <div className="w-8 h-8 border-2 rounded-full animate-spin"
          style={{ borderColor: `${tc}30`, borderTopColor: pc }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: bg, color: tc }} data-testid="privacy-policy-page">
      {/* Top bar */}
      <header className="sticky top-0 z-10 border-b" style={{ backgroundColor: `${bg}EE`, backdropFilter: 'blur(8px)', borderColor: `${tc}10` }}>
        <div className="max-w-3xl mx-auto px-5 py-3 flex items-center gap-3">
          <button
            onClick={() => window.history.length > 1 ? window.history.back() : (window.location.href = '/')}
            data-testid="privacy-back-btn"
            className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center transition-colors"
            style={{ backgroundColor: `${tc}10`, color: tc }}
          >
            <ArrowLeft size={18} />
          </button>
          <div className="flex-1 min-w-0">
            <p className="text-xs opacity-60 uppercase tracking-wider">Informativa</p>
            <h1 className="text-base font-bold truncate" data-testid="privacy-business-name">{businessName}</h1>
          </div>
          {fileUrl && (
            <a
              href={fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="privacy-pdf-link"
              className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all active:scale-95"
              style={{ backgroundColor: `${pc}20`, color: pc, border: `1px solid ${pc}40` }}
            >
              <Download size={12} /> PDF
            </a>
          )}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-8">
        {/* Hero */}
        <div className="mb-8 flex items-start gap-4">
          <div className="flex-shrink-0 w-12 h-12 rounded-2xl flex items-center justify-center"
            style={{ backgroundColor: `${pc}20`, color: pc }}>
            <ShieldCheck size={22} />
          </div>
          <div>
            <h2 className="text-2xl font-bold leading-tight" style={{ color: tc }}>Informativa Privacy</h2>
            <p className="text-sm mt-1" style={{ color: ts }}>
              Trattamento dati ai sensi del Regolamento UE 2016/679 (GDPR).
              {data?.privacy_policy_version_number ? ` Versione ${data.privacy_policy_version_number}.` : ''}
            </p>
          </div>
        </div>

        {error && (
          <div className="rounded-xl p-4 mb-6 border"
            style={{ backgroundColor: '#FF555510', borderColor: '#FF555540', color: '#FF8888' }}
            data-testid="privacy-error">
            {error}
          </div>
        )}

        {/* Structured sections — preferred render */}
        {sections.length > 0 && (
          <div className="space-y-6" data-testid="privacy-sections">
            {sections.map((sec, i) => (
              <section key={i} className="rounded-2xl p-5 border"
                style={{ backgroundColor: `${tc}05`, borderColor: `${tc}10` }}>
                <h3 className="text-base font-bold mb-3" style={{ color: pc }}>{sec.title}</h3>
                <div className="space-y-2.5 text-sm leading-relaxed" style={{ color: ts }}>
                  {(sec.paragraphs || []).map((p, j) => (
                    <p key={j}>{p}</p>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}

        {/* Fallback: only the PDF/external link is configured */}
        {sections.length === 0 && (fileUrl || externalUrl) && (
          <div className="rounded-2xl p-6 border text-center"
            style={{ backgroundColor: `${tc}05`, borderColor: `${tc}10` }}
            data-testid="privacy-fallback">
            <FileText size={36} className="mx-auto mb-3 opacity-50" />
            <p className="text-sm mb-4" style={{ color: ts }}>
              La privacy policy è disponibile come documento separato.
            </p>
            <a
              href={fileUrl || externalUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="privacy-fallback-link"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all active:scale-95"
              style={{ backgroundColor: pc, color: '#fff' }}
            >
              <Download size={14} /> Apri documento
            </a>
          </div>
        )}

        {/* Empty state — admin hasn't configured anything */}
        {sections.length === 0 && !fileUrl && !externalUrl && !error && (
          <div className="rounded-2xl p-8 text-center border"
            style={{ backgroundColor: `${tc}05`, borderColor: `${tc}10`, color: ts }}
            data-testid="privacy-empty">
            <FileText size={36} className="mx-auto mb-3 opacity-40" />
            <p className="text-sm">L'informativa privacy non è ancora disponibile.</p>
          </div>
        )}

        {/* PDF download button (mobile, in-page) */}
        {fileUrl && (
          <div className="mt-8 sm:hidden">
            <a
              href={fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="privacy-pdf-link-mobile"
              className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-sm font-semibold transition-all active:scale-95"
              style={{ backgroundColor: `${pc}20`, color: pc, border: `1px solid ${pc}40` }}
            >
              <Download size={14} /> Scarica PDF completo
            </a>
          </div>
        )}
      </main>
    </div>
  );
}
